import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

// The PROPERTY (the house) keeps the home's facts; the OWNERSHIP timeline records who's
// been here and when. Equipment + jobs attach to a property. See
// research/property-ownership-lifecycle.md.

export interface Property {
  id: string;
  address: string;
  city: string | null;
  esc_custno: string | null;
  esc_locno: string | null;
  origin: string;            // esc-location | nc-job | manual
  origin_job_id: string | null;
  created_at: string;
}

export interface Ownership {
  id: string;
  property_id: string;
  owner_id: string | null;   // → pegasus_owner identity
  party_custno: string | null;
  party_name: string;
  role: string;              // owner | tenant | builder
  start_date: string | null;
  end_date: string | null;   // null = current tenure
  source: string | null;     // sale | new-construction | move | tenant-change | migration
  created_at: string;
}

/** A first-class owner identity (person/business), separate from the property. */
export interface Owner {
  id: string;
  name: string;
  phone1: string | null; phone2: string | null;
  email1: string | null; email2: string | null;
  notes: string | null;
  source: string;            // pegasus | esc-snapshot
  esc_custno: string | null;
  created_at: string;
}

/** Property + its ownership timeline for a job (via job.property_id). */
export function useJobProperty(jobId: string | null, propertyId: string | null | undefined) {
  return useQuery({
    queryKey: ["job_property", jobId, propertyId],
    enabled: !!jobId,
    queryFn: async (): Promise<{ property: Property | null; ownership: Ownership[] }> => {
      if (!propertyId) return { property: null, ownership: [] };
      const [{ data: p }, { data: o }] = await Promise.all([
        supabase.from("pegasus_property").select("*").eq("id", propertyId).maybeSingle(),
        supabase.from("pegasus_property_ownership").select("*").eq("property_id", propertyId)
          .order("start_date", { ascending: true, nullsFirst: true }),
      ]);
      return { property: (p as Property) ?? null, ownership: (o ?? []) as Ownership[] };
    },
  });
}

export interface PreviousOwner { owner: Owner; ownership: Ownership }

/**
 * Attribute a record (by its date) to a previous owner when it predates the current owner's
 * tenure — the handoff date is the hinge. Returns the prior owner's name, or null when the
 * record is the current owner's (or there's been no transfer). Lets any 360 surface badge
 * equipment / invoices / dispatches / quotes old-vs-new without touching the ESC mirror.
 */
export function priorOwnerForDate(
  dateStr: string | null | undefined,
  currentOwnerStart: string | null | undefined,
  previousOwners: PreviousOwner[],
): string | null {
  if (!dateStr || !currentOwnerStart || previousOwners.length === 0) return null;
  if (dateStr >= currentOwnerStart) return null; // the current owner's era — no badge
  const hit = previousOwners.find((p) => {
    const s = p.ownership.start_date, e = p.ownership.end_date;
    return (!s || dateStr >= s) && (!e || dateStr < e);
  });
  return (hit ?? previousOwners[0]).owner.name; // previousOwners[0] = most-recent prior owner
}

/** The recorded property + ownership timeline + the current owner + the archived previous owners. */
export function useLocationOwnership(custno: string | null | undefined, locno: string | null | undefined) {
  return useQuery({
    queryKey: ["location_ownership", custno, locno],
    enabled: !!custno,
    queryFn: async (): Promise<{ property: Property | null; ownership: Ownership[]; currentOwner: Owner | null; previousOwners: PreviousOwner[] }> => {
      // Match tolerantly: exact locno first, then a null-locno property, then
      // whatever exists — a transfer recorded with/without a location must
      // still light up the timeline + badges (Seth 2026-08-21).
      const { data: props } = await supabase.from("pegasus_property").select("*").eq("esc_custno", custno!);
      const all = (props ?? []) as Property[];
      const property = (locno ? all.find((p) => p.esc_locno === locno) : null)
        ?? all.find((p) => !p.esc_locno)
        ?? all[0]
        ?? null;
      if (!property) return { property: null, ownership: [], currentOwner: null, previousOwners: [] };
      const { data: o } = await supabase.from("pegasus_property_ownership").select("*")
        .eq("property_id", property.id).order("start_date", { ascending: true, nullsFirst: true });
      const ownership = (o ?? []) as Ownership[];
      // resolve every linked owner identity in one query
      const ownerIds = [...new Set(ownership.map((r) => r.owner_id).filter(Boolean))] as string[];
      const byId = new Map<string, Owner>();
      if (ownerIds.length) {
        const { data: ows } = await supabase.from("pegasus_owner").select("*").in("id", ownerIds);
        for (const ow of (ows ?? []) as Owner[]) byId.set(ow.id, ow);
      }
      // The biller is the OWNER — prefer it over an open tenant tenure (a rental
      // can have both an open owner AND an open tenant tenure).
      const open = ownership.find((r) => !r.end_date && r.role === "owner")
        ?? ownership.find((r) => !r.end_date && r.role !== "builder");
      const currentOwner = open?.owner_id ? byId.get(open.owner_id) ?? null : null;
      const previousOwners: PreviousOwner[] = ownership
        .filter((r) => r.end_date && r.role !== "builder" && r.owner_id && byId.has(r.owner_id))
        .map((r) => ({ owner: byId.get(r.owner_id!)!, ownership: r }))
        .reverse(); // most-recent previous owner first
      return { property, ownership, currentOwner, previousOwners };
    },
  });
}

export interface NewOwnerInput {
  name: string;
  phone1?: string | null; phone2?: string | null;
  email1?: string | null; email2?: string | null;
  notes?: string | null;
}
export interface TransferOwnerInput {
  custno: string; locno: string | null;
  oldOwnerName: string;        // current ESC account display name (frozen snapshot)
  /** NEW-OWNER-CONTACTS (34i87): the outgoing owner's lines, frozen onto their
   *  previous-owner record so "who did we used to call?" survives the handoff. */
  oldOwnerLines?: { phone1: string | null; phone2: string | null; email1: string | null; email2: string | null; notes: string | null } | null;
  newOwner: NewOwnerInput;
  /** "sale" = the home sold (archive the old owner, new owner takes the account).
   *  "tenant" = a rental turnover (owner keeps billing, only the occupant changes). */
  kind?: "sale" | "tenant";
}

/**
 * The robust owner transfer (a home sells; the new owner calls). Captures the new owner's
 * FULL identity, snapshots the old owner as a frozen Previous-owner record (never deleted,
 * always viewable), re-points the property, and stamps the handoff date (which later
 * attributes every record old-vs-new by date). The home's equipment/warranty/access/history
 * carry to the new owner; the old owner's money/agreements/messages stay with them. The new
 * owner is a Pegasus-native identity — the first place Pegasus owns the customer record.
 */
export function useTransferOwner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: TransferOwnerInput) => {
      const { data: u } = await supabase.auth.getUser();
      const email = u?.user?.email ?? null;
      const today = new Date().toISOString().slice(0, 10);

      // 1. find-or-create the property for this address (real address from ESC).
      let pq = supabase.from("pegasus_property").select("id").eq("esc_custno", v.custno);
      pq = v.locno ? pq.eq("esc_locno", v.locno) : pq.is("esc_locno", null);
      const { data: found } = await pq.limit(1);
      let propertyId = found?.[0]?.id as string | undefined;
      if (!propertyId) {
        let addr: string | null = null, city: string | null = null;
        if (v.locno) {
          const { data: loc } = await supabase.from("mirror_location").select("add1,city")
            .eq("custno", v.custno).eq("locno", v.locno).maybeSingle();
          addr = loc?.add1 ?? null; city = loc?.city ?? null;
        }
        const { data: prop, error } = await supabase.from("pegasus_property").insert({
          address: addr ?? "(unknown)", city, esc_custno: v.custno, esc_locno: v.locno,
          origin: "esc-location", created_by: email,
        }).select("id").single();
        if (error) throw error;
        propertyId = prop.id;
      }

      // TENANT change (not a sale): the OWNER keeps billing + agreements; only
      // the occupant changes. Record a tenant tenure alongside the still-open
      // owner tenure — nothing archived, the owner is untouched.
      if ((v.kind ?? "sale") === "tenant") {
        const n = v.newOwner;
        const { data: tenant, error: te } = await supabase.from("pegasus_owner").insert({
          name: n.name.trim(), phone1: n.phone1 || null, phone2: n.phone2 || null,
          email1: n.email1 || null, email2: n.email2 || null, notes: n.notes || null,
          source: "pegasus", created_by: email,
        }).select("id").single();
        if (te) throw te;
        // A turnover ends the PRIOR tenant's tenure; the owner's stays open.
        await supabase.from("pegasus_property_ownership").update({ end_date: today })
          .eq("property_id", propertyId).is("end_date", null).eq("role", "tenant");
        const { error: oe } = await supabase.from("pegasus_property_ownership").insert({
          property_id: propertyId, owner_id: tenant.id, party_name: n.name.trim(),
          role: "tenant", start_date: today, source: "tenant-change", created_by: email,
        });
        if (oe) throw oe;
        return { propertyId, newOwnerId: tenant.id };
      }

      // 2. snapshot the OLD owner (frozen, WITH the lines we called them on) +
      //    ensure they hold the open tenure, then end it.
      const snapLines: Partial<NonNullable<TransferOwnerInput["oldOwnerLines"]>> = v.oldOwnerLines ?? {};
      const { data: openRows } = await supabase.from("pegasus_property_ownership")
        .select("id, owner_id").eq("property_id", propertyId).is("end_date", null).limit(1);
      if (!openRows?.length) {
        const { data: snap } = await supabase.from("pegasus_owner").insert({
          name: v.oldOwnerName, source: "esc-snapshot", esc_custno: v.custno, created_by: email, ...snapLines,
        }).select("id").single();
        await supabase.from("pegasus_property_ownership").insert({
          property_id: propertyId, owner_id: snap?.id ?? null, party_custno: v.custno,
          party_name: v.oldOwnerName, role: "owner", source: "migration", created_by: email,
        });
      } else if (!openRows[0].owner_id) {
        const { data: snap } = await supabase.from("pegasus_owner").insert({
          name: v.oldOwnerName, source: "esc-snapshot", esc_custno: v.custno, created_by: email, ...snapLines,
        }).select("id").single();
        await supabase.from("pegasus_property_ownership").update({ owner_id: snap?.id ?? null })
          .eq("id", openRows[0].id);
      } else if (v.oldOwnerLines) {
        // The outgoing owner already has an identity row (an earlier migration
        // snapshot or a Pegasus-native owner selling on): fill in only the
        // slots it does not have yet — never overwrite what someone typed.
        const { data: cur } = await supabase.from("pegasus_owner").select("phone1, phone2, email1, email2, notes")
          .eq("id", openRows[0].owner_id).maybeSingle();
        if (cur) {
          const patch: Record<string, string | null> = {};
          if (!cur.phone1 && snapLines.phone1) patch.phone1 = snapLines.phone1;
          if (!cur.phone2 && snapLines.phone2) patch.phone2 = snapLines.phone2;
          if (!cur.email1 && snapLines.email1) patch.email1 = snapLines.email1;
          if (!cur.email2 && snapLines.email2) patch.email2 = snapLines.email2;
          if (!cur.notes && snapLines.notes) patch.notes = snapLines.notes;
          if (Object.keys(patch).length) await supabase.from("pegasus_owner").update(patch).eq("id", openRows[0].owner_id);
        }
      }
      await supabase.from("pegasus_property_ownership").update({ end_date: today })
        .eq("property_id", propertyId).is("end_date", null);

      // 3. the NEW owner identity + their open tenure (handoff date = today).
      const n = v.newOwner;
      const { data: newOwner, error: noErr } = await supabase.from("pegasus_owner").insert({
        name: n.name.trim(), phone1: n.phone1 || null, phone2: n.phone2 || null,
        email1: n.email1 || null, email2: n.email2 || null, notes: n.notes || null,
        source: "pegasus", created_by: email,
      }).select("id").single();
      if (noErr) throw noErr;
      const { error: oe } = await supabase.from("pegasus_property_ownership").insert({
        property_id: propertyId, owner_id: newOwner.id, party_name: n.name.trim(),
        role: "owner", start_date: today, source: "sale", created_by: email,
      });
      if (oe) throw oe;

      // 4. Archive the OLD owner's access/preference signals (MIG-NOTES) — gate
      //    codes, prefs, hazards are the prior owner's and must not silently
      //    follow the new owner. The office can carry physical-property ones
      //    (gate/lockbox) forward from the archived list. Scoped to the sold
      //    location (+ customer-level notes); other locations untouched.
      {
        let aq = supabase
          .from("extracted_signal")
          .update({ archived_at: new Date().toISOString(), archived_for: v.oldOwnerName })
          .eq("custno", v.custno)
          .is("archived_at", null);
        if (v.locno) aq = aq.or(`locno.eq.${v.locno},locno.is.null`);
        await aq;
      }
      return { propertyId, newOwnerId: newOwner.id };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["location_ownership"] });
      qc.invalidateQueries({ queryKey: ["customer_signals"] });
      // The recorded handoff changes what the 360 SAYS about this customer:
      // the AI snapshot regenerates ownership-aware (summarize-location v11
      // hashes the owner + eras), the prose line flips to recent-buyer, and
      // the timeline gains the handoff event.
      qc.invalidateQueries({ queryKey: ["location_notes_summary"] });
      qc.invalidateQueries({ queryKey: ["customer_metrics"] });
      qc.invalidateQueries({ queryKey: ["timeline_extras"] });
    },
  });
}

export interface HandoffJob {
  id: string; custno: string; customer_name?: string | null;
  site_address?: string | null; site_city?: string | null;
  address?: string | null; property_id?: string | null;  // resolved address = fallback when no explicit site
}

/**
 * New-construction → homeowner handoff. Ensures the property + the builder's tenure exist,
 * ends the open (builder) tenure, starts the homeowner's, and re-homes the installed
 * equipment to the property. The property keeps everything; the builder's construction
 * billing stays on the builder account. Reversible — nothing is deleted.
 */
export function useNewHomeownerHandoff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ job, homeowner_name }: { job: HandoffJob; homeowner_name: string }) => {
      const { data: u } = await supabase.auth.getUser();
      const email = u?.user?.email ?? null;
      const today = new Date().toISOString().slice(0, 10);

      // 1. Ensure a property (+ builder tenure) exists for this job.
      let propertyId = job.property_id ?? null;
      if (!propertyId) {
        const { data: prop, error } = await supabase.from("pegasus_property").insert({
          address: job.site_address ?? job.address ?? "(unknown)", city: job.site_city ?? null,
          esc_custno: job.custno, origin: "nc-job", origin_job_id: job.id, created_by: email,
        }).select().single();
        if (error) throw error;
        propertyId = prop.id;
        await supabase.from("pegasus_job").update({ property_id: propertyId }).eq("id", job.id);
        await supabase.from("pegasus_property_ownership").insert({
          property_id: propertyId, party_custno: job.custno, party_name: job.customer_name ?? "Builder",
          role: "builder", source: "new-construction", created_by: email,
        });
      }
      // 2. End the open tenure (the builder); start the homeowner's.
      await supabase.from("pegasus_property_ownership").update({ end_date: today })
        .eq("property_id", propertyId).is("end_date", null);
      const { error: oe } = await supabase.from("pegasus_property_ownership").insert({
        property_id: propertyId, party_name: homeowner_name.trim(), role: "owner",
        start_date: today, source: "new-construction", created_by: email,
      });
      if (oe) throw oe;
      // 3. Re-home installed equipment for this job to the property (it's the house's now).
      await supabase.from("pegasus_equipment").update({ property_id: propertyId }).eq("job_id", job.id);
      return { propertyId };
    },
    onSuccess: (_r, { job }) => {
      qc.invalidateQueries({ queryKey: ["job_property"] });
      qc.invalidateQueries({ queryKey: ["job", job.id] });
      qc.invalidateQueries({ queryKey: ["jobs"] });
    },
  });
}
